import { supabase, unwrap } from './supabaseClient'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './config'

/**
 * Kaller en edge function. Brukes til de operasjonene som trenger
 * service_role: PIN-innlogging og oppretting/administrasjon av barn.
 */
async function callFunction(name, body, { authenticated = true } = {}) {
  const headers = { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY }

  if (authenticated) {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw new Error('Ikke innlogget')
    headers.Authorization = `Bearer ${data.session.access_token}`
  } else {
    headers.Authorization = `Bearer ${SUPABASE_ANON_KEY}`
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })

  const text = await res.text()
  let payload = {}
  try { payload = text ? JSON.parse(text) : {} } catch { /* ikke JSON */ }

  if (!res.ok) throw new Error(payload.error || `Feil fra tjeneren (${res.status})`)
  return payload
}

export const api = {
  isDemo: false,

  // --- Autentisering ------------------------------------------------------

  async getSession() {
    return unwrap(await supabase.auth.getSession()).session
  },

  onAuthChange(callback) {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => callback(session))
    return () => data.subscription.unsubscribe()
  },

  /**
   * getSession() leser bare tokenet som ligger lagret lokalt, og sier ingenting
   * om brukeren fortsatt finnes. Denne spør tjeneren. Uten den blir en bruker
   * som er slettet - eller et token fra før databasen ble gjenopprettet -
   * sittende «innlogget» til tokenet utløper, og møter uforståelige feil.
   */
  async verifyUser() {
    const { data, error } = await supabase.auth.getUser()
    return Boolean(data?.user) && !error
  },

  async signUp({ email, password }) {
    const data = unwrap(await supabase.auth.signUp({ email, password }))
    // Er e-postbekreftelse påslått i Supabase, får vi bruker uten session.
    return { needsConfirmation: !data.session, session: data.session }
  },

  async signIn({ email, password }) {
    return unwrap(await supabase.auth.signInWithPassword({ email, password })).session
  },

  async sendPasswordReset(email) {
    unwrap(await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${location.origin}/reset-password`,
    }))
  },

  async updatePassword(password) {
    unwrap(await supabase.auth.updateUser({ password }))
  },

  async signOut() {
    await supabase.auth.signOut()
  },

  /** Barnas innloggingsskjerm: hent profilene i familien ut fra familiekoden. */
  async listChildrenByFamilyCode(familyCode) {
    return callFunction('child-login', { action: 'list', family_code: familyCode }, { authenticated: false })
  },

  async childLogin({ childId, pin }) {
    const result = await callFunction(
      'child-login',
      { action: 'login', profile_id: childId, pin },
      { authenticated: false },
    )
    // Edge-funksjonen returnerer en engangslenke vi bytter i en ekte session,
    // slik at RLS fungerer helt likt for barn og foreldre.
    const data = unwrap(
      await supabase.auth.verifyOtp({ token_hash: result.token_hash, type: 'magiclink' }),
    )
    return data.session
  },

  // --- Oppsett av familie -------------------------------------------------

  async createFamily({ familyName, displayName }) {
    return unwrap(await supabase.rpc('create_family', {
      p_family_name: familyName,
      p_display_name: displayName,
    }))
  },

  async joinFamily({ code, displayName }) {
    return unwrap(await supabase.rpc('join_family', {
      p_code: code,
      p_display_name: displayName,
    }))
  },

  async createInvite() {
    return unwrap(await supabase.rpc('create_invite'))
  },

  // --- Grunndata ----------------------------------------------------------

  async loadProfile() {
    const { data } = await supabase.auth.getSession()
    if (!data.session) return null
    const rows = unwrap(
      await supabase.from('profiles').select('*').eq('id', data.session.user.id).limit(1),
    )
    return rows[0] || null
  },

  async loadFamily() {
    const rows = unwrap(await supabase.from('families').select('*').limit(1))
    return rows[0] || null
  },

  async loadMembers() {
    return unwrap(
      await supabase.from('profiles').select('*').order('role').order('sort_order').order('display_name'),
    )
  },

  async loadBalances() {
    return unwrap(await supabase.from('child_balances').select('*'))
  },

  async updateFamily(patch) {
    const family = await this.loadFamily()
    unwrap(await supabase.from('families').update(patch).eq('id', family.id))
  },

  async updateProfile(id, patch) {
    unwrap(await supabase.from('profiles').update(patch).eq('id', id))
  },

  // --- Barn (krever service_role) -----------------------------------------

  async createChild({ displayName, emoji, color, pin }) {
    return callFunction('child-admin', {
      action: 'create', display_name: displayName, emoji, color, pin,
    })
  },

  async setChildPin(childId, pin) {
    return callFunction('child-admin', { action: 'set_pin', profile_id: childId, pin })
  },

  async deleteChild(childId) {
    return callFunction('child-admin', { action: 'delete', profile_id: childId })
  },

  // --- Fast lommepenger ---------------------------------------------------

  /** period: 'none' | 'weekly' | 'monthly'. day er ukedag 1-7 eller dato 1-28. */
  async setChildAllowance({ childId, period, amountOre, day }) {
    return unwrap(await supabase.rpc('set_child_allowance', {
      p_child_id: childId,
      p_period: period,
      p_amount_ore: amountOre ?? 0,
      p_day: day ?? 1,
    }))
  },

  /**
   * Fører de faste lommepengene som har forfalt. Returnerer antall
   * posteringer, slik at appen vet om saldoene må hentes på nytt.
   */
  async runDueAllowances() {
    return Number(unwrap(await supabase.rpc('run_due_allowances'))) || 0
  },

  /**
   * Registrerer at den innloggede var innom, til statistikken.
   *
   * Foreldre kunne vært talt gjennom auth.users.last_sign_in_at, men barna
   * logger inn med PIN gjennom en edge function og etterlater seg ingenting
   * der. Dette er derfor det eneste stedet et barns bruk blir synlig.
   * Funksjonen skriver høyst én gang i timen per person.
   */
  async touchLastSeen() {
    await supabase.rpc('touch_last_seen')
  },

  // --- Kategorier ---------------------------------------------------------

  async listCategories() {
    return unwrap(await supabase.from('task_categories').select('*').order('sort_order'))
  },

  async createCategory(payload) {
    const family = await this.loadFamily()
    return unwrap(
      await supabase.from('task_categories').insert({ ...payload, family_id: family.id }).select().single(),
    )
  },

  async updateCategory(id, patch) {
    unwrap(await supabase.from('task_categories').update(patch).eq('id', id))
  },

  async deleteCategory(id) {
    unwrap(await supabase.from('task_categories').delete().eq('id', id))
  },

  // --- Oppgaver -----------------------------------------------------------

  async listTasks() {
    return unwrap(await supabase.from('tasks').select('*').order('sort_order').order('created_at'))
  },

  async createTask(payload) {
    const family = await this.loadFamily()
    return unwrap(
      await supabase.from('tasks').insert({ ...payload, family_id: family.id }).select().single(),
    )
  },

  async updateTask(id, patch) {
    unwrap(await supabase.from('tasks').update(patch).eq('id', id))
  },

  async deleteTask(id) {
    unwrap(await supabase.from('tasks').delete().eq('id', id))
  },

  // --- Registreringer -----------------------------------------------------

  async listCompletions({ status, childId, limit = 100 } = {}) {
    let query = supabase.from('task_completions').select('*').order('created_at', { ascending: false }).limit(limit)
    if (status) query = query.eq('status', status)
    if (childId) query = query.eq('child_id', childId)
    return unwrap(await query)
  },

  async submitCompletion(taskId, note) {
    return unwrap(await supabase.rpc('submit_completion', { p_task_id: taskId, p_note: note || null }))
  },

  /** Forelder fører opp et gjøremål for et barn. Godkjent med én gang. */
  async submitCompletionFor(childId, taskId, note) {
    return unwrap(await supabase.rpc('submit_completion_for', {
      p_child_id: childId,
      p_task_id: taskId,
      p_note: note || null,
    }))
  },

  async decideCompletion(id, approve) {
    return unwrap(await supabase.rpc('decide_completion', { p_completion_id: id, p_approve: approve }))
  },

  async undoCompletion(id) {
    unwrap(await supabase.rpc('undo_completion', { p_completion_id: id }))
  },

  // --- Utfordringer -------------------------------------------------------

  async loadChallengeData() {
    const [challenges, tasks, participants, standings, prizes] = await Promise.all([
      supabase.from('challenges').select('*').order('ends_on', { ascending: false }),
      supabase.from('challenge_tasks').select('*'),
      supabase.from('challenge_participants').select('*'),
      supabase.from('challenge_standings').select('*'),
      supabase.from('challenge_prizes').select('*'),
    ])
    return {
      challenges: unwrap(challenges),
      tasks: unwrap(tasks),
      participants: unwrap(participants),
      standings: unwrap(standings),
      prizes: unwrap(prizes),
    }
  },

  async saveChallenge(payload) {
    return unwrap(await supabase.rpc('save_challenge', {
      p_id: payload.id ?? null,
      p_title: payload.title,
      p_description: payload.description ?? null,
      p_emoji: payload.emoji ?? '🏆',
      p_starts_on: payload.starts_on,
      p_ends_on: payload.ends_on,
      p_goal_type: payload.goal_type,
      p_goal_target: payload.goal_target ?? null,
      p_prize_ore: payload.prize_ore ?? 0,
      p_task_ids: payload.task_ids,
      p_participants: payload.participants,
    }))
  },

  async deleteChallenge(id) {
    unwrap(await supabase.rpc('delete_challenge', { p_id: id }))
  },

  async payChallengePrize(challengeId, childId) {
    return unwrap(await supabase.rpc('pay_challenge_prize', {
      p_challenge_id: challengeId,
      p_child_id: childId,
    }))
  },

  async undoChallengePrize(challengeId, childId) {
    unwrap(await supabase.rpc('undo_challenge_prize', {
      p_challenge_id: challengeId,
      p_child_id: childId,
    }))
  },

  // --- Penger -------------------------------------------------------------

  async listTransactions({ childId, limit = 100 } = {}) {
    let query = supabase.from('transactions').select('*').order('created_at', { ascending: false }).limit(limit)
    if (childId) query = query.eq('child_id', childId)
    return unwrap(await query)
  },

  async addTransaction({ childId, type, amountOre, description, spendCategory }) {
    return unwrap(await supabase.rpc('add_transaction', {
      p_child_id: childId,
      p_type: type,
      p_amount_ore: amountOre,
      p_description: description || '',
      p_spend_category: spendCategory || null,
    }))
  },

  // --- Barnets egne uttak -------------------------------------------------

  async listWithdrawals({ status, childId, limit = 200 } = {}) {
    let query = supabase.from('child_withdrawals').select('*')
      .order('created_at', { ascending: false }).limit(limit)
    if (status) query = query.eq('status', status)
    if (childId) query = query.eq('child_id', childId)
    return unwrap(await query)
  },

  async submitWithdrawal({ amountOre, description, spendCategory }) {
    return unwrap(await supabase.rpc('submit_withdrawal', {
      p_amount_ore: amountOre,
      p_description: description || null,
      p_spend_category: spendCategory || null,
    }))
  },

  async decideWithdrawal(id, approve) {
    return unwrap(await supabase.rpc('decide_withdrawal', { p_id: id, p_approve: approve }))
  },

  async undoWithdrawal(id) {
    unwrap(await supabase.rpc('undo_withdrawal', { p_id: id }))
  },

  async deleteTransaction(id) {
    unwrap(await supabase.rpc('delete_transaction', { p_id: id }))
  },

  /** Retter beløpet og teksten på én postering. Stjernene røres ikke. */
  async updateTransaction(id, amountOre, description) {
    return unwrap(await supabase.rpc('update_transaction', {
      p_id: id,
      p_amount_ore: amountOre,
      // null betyr «la teksten stå», tom streng betyr «fjern den».
      p_description: description ?? null,
    }))
  },
}
